import {
  isApiEnabled,
  sendSignedRequest,
  waitForDeviceSession,
} from '@muzly/api-client';
import type { HttpError } from '@muzly/api-client';

import {
  analySessionGeneration,
  loadAnalySessionId,
  saveAnalySessionId,
} from './analySessionStore';

import type { AnalySubmitBody, AnalyView } from './analyContract';
import type { AnalyTransport, TransportResult } from './analySession';

/**
 * analyTransport — analy 계약의 **네트워크 절반**. 순수 매퍼(`toAnalySubmitBody`)·큐
 * (`analySession`)와 파일로 갈라 둔다: 이쪽은 브라우저 IO라 단위 테스트 대상이 아니다
 * (admin의 `runSampleAnalysis` / `decodeSample` 분리와 같은 규칙).
 *
 * **주소는 muzly 백엔드 하나뿐이다.** `/oauth/*`·`/profile/*`과 같은 base(`API_ENDPOINT`)
 * 아래에 `/analy/*`가 산다(서버 SPEC §3 라우트표). 그래서 analy 전용 베이스 URL을 따로 두지
 * 않는다 — 두면 같은 주소가 두 벌이 되어 한쪽만 바뀌는 날이 온다. 끄는 스위치도 같다:
 * `API_ENDPOINT`가 비면 요청이 한 건도 안 나간다.
 */

/** 서명이 서기를 기다리는 상한. 넘으면 이번 기회는 포기한다(다음 제출이 다시 시도한다). */
const SESSION_WAIT_MS = 2_000;

/**
 * 만드는 중인 루트. 인트로 CTA와 첫 테이크 제출이 겹쳐도 `POST`는 한 번만 나간다.
 *
 * 세대를 같이 쥔다 — 이 생성이 시작된 회차가 그 사이 마감됐다면(`clearAnalySessionId`)
 * 다음 회차가 그 결과를 물려받으면 안 된다. 물려받으면 이미 `done`인 루트에 제출해
 * 409로 잠긴다(`analySessionStore`의 세대 주석).
 */
let creating: { generation: number; promise: Promise<string | null> } | null =
  null;

/**
 * `POST /analy/0` — 진단 루트를 만든다. body는 빈 객체를 보낸다(본문이 없으면 프레임워크가
 * 400을 던진다 — SPEC §3.1).
 *
 * **재시도하지 않는다.** `analyNo`를 원자적으로 올려 id를 만드는 비멱등 요청이고 멱등키가
 * 계약에 없다(D28) — 다시 보내면 진단이 하나 더 생긴다. 실패하면 이번 회차의 서버 기록을
 * 포기하고 로컬로만 진행한다.
 */
const createRoot = async (generation: number): Promise<string | null> => {
  // 세션 없이 보내면 서명이 없어 403이다. 그 403은 재시도로 안 풀린다 — 기다리는 게 맞다.
  if (!(await waitForDeviceSession(SESSION_WAIT_MS))) {
    return null;
  }
  try {
    const view = await sendSignedRequest<AnalyView>({
      method: 'POST',
      path: '/analy/0',
      body: {},
    });
    if (!view.id) {
      return null;
    }
    // 저장은 세대가 그대로일 때만 — 마감 뒤에 도착한 생성은 id를 **반환만** 하고
    // 남기지 않는다. 기다리던 마감 제출은 그 반환값으로 온전히 나간다.
    saveAnalySessionId(view.id, generation);
    return view.id;
  } catch {
    return null;
  }
};

/**
 * 이 회차의 루트 id — 있으면 그대로, 없으면 한 번 만든다.
 *
 * 부르는 자리가 둘이다: 인트로 CTA(플로우가 이탈해도 그때까지가 기록에 남게)와 첫 테이크
 * 제출(인트로에서 세션이 아직 안 섰던 회차의 유일한 만회 기회). 그래서 **단일 비행**이다.
 */
export const ensureAnalySessionId = (): Promise<string | null> => {
  const stored = loadAnalySessionId();
  if (stored !== null) {
    return Promise.resolve(stored);
  }
  if (!isApiEnabled()) {
    return Promise.resolve(null);
  }
  const generation = analySessionGeneration();
  if (creating === null || creating.generation !== generation) {
    const promise = createRoot(generation);
    creating = { generation, promise };
    void promise.finally(() => {
      if (creating?.promise === promise) {
        creating = null;
      }
    });
  }
  return creating.promise;
};

/**
 * `GET /analy/0/list?state=done&detail=1&limit=250` — 복원용 done 목록.
 *
 * 응답 봉투 `{ list, total }`는 lemon-core `ListResult<T>` 관례(기본 리스트 응답 계약)를
 * 따른 것으로, 이 앱의 dev 스테이지 실측(NOTE, 2026-09-07 muz-d1)이 직접 확인한 것은 개별
 * `AnalyView` 항목의 모양뿐이다 — 봉투 자체는 실기기 목록 호출로 아직 검증되지 않았다.
 *
 * `state=done`을 붙여도(실측: 서버가 이 필터를 무시한다) 그 회차의 하위 4건(`stereo` 있음)이
 * 같은 배열에 섞여 오고 `total`도 하위까지 센다 — 그래서 `limit`을 250(회차당 문서 5개 ×
 * 이력 상한 50)으로 넉넉히 잡는다. 메인·done만 남기는 것은 이 함수가 아니라 순수 매퍼
 * `fromAnalyView`의 일이다(이 파일은 IO만 — 파일 헤더 주석).
 *
 * 실패·세션 미확보는 `null`이다 — 복원은 부팅 경로라 여기서 재시도하지 않는다
 * (`restoreDiagnoses`가 상한을 다시 감싼다).
 */
export interface AnalyListResult {
  list: AnalyView[];
  total?: number;
}

export const listDone = async (): Promise<AnalyView[] | null> => {
  if (!isApiEnabled()) {
    return null;
  }
  if (!(await waitForDeviceSession(SESSION_WAIT_MS))) {
    return null;
  }
  try {
    const result = await sendSignedRequest<AnalyListResult>({
      method: 'GET',
      path: '/analy/0/list',
      // 쿼리는 `params` 로 — `path` 에 붙이면 서명이 어긋난다(`SignedRequestInit.params`).
      params: { state: 'done', detail: 1, limit: 250 },
    });
    return result.list ?? [];
  } catch {
    return null;
  }
};

/**
 * `PUT /analy/{id}/submit` — 테이크 하나. 실패는 **상태코드로만** 판정한다(에러 body 스키마가
 * 계약에 없다). 던지지 않고 결과로 돌려주는 것이 `analySession`의 재시도 판정 입력이다.
 *
 * id는 인코딩하지 않는다 — `<uid>:<analyNo>`의 콜론은 경로 구획에 그대로 쓸 수 있고
 * (RFC 3986 `pchar`), 퍼센트로 바꾸면 서버 캐치올 라우트가 다른 id로 읽는다.
 */
export const createAnalyTransport = (): AnalyTransport | null =>
  isApiEnabled()
    ? {
        submit: async (
          analyId,
          body: AnalySubmitBody,
        ): Promise<TransportResult> => {
          try {
            await sendSignedRequest({
              method: 'PUT',
              path: `/analy/${analyId}/submit`,
              body,
            });
            return { ok: true };
          } catch (error) {
            // `sendSignedRequest`는 실패를 언제나 `HttpError`로 세운다 — axios 예외든
            // lemon-core 봉투든 상태코드가 같은 자리에 있다. 여기서 다시 손으로 캐스트하면
            // 그 계약을 한 번 더 베끼는 것이고, 봉투 해석이 바뀌는 날 이쪽만 남는다.
            return { ok: false, status: (error as HttpError).status };
          }
        },
      }
    : null;
